import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { usePortal } from '@fourthwall/utils/dist/hooks/usePortal'

import { usePlatformFeatureFlags } from '../../components/utils/DashboardFeatureFlags/usePlatformFeatureFlags'
import { useAdminModeContext } from '../App/providers/AdminModeProvider'

type AIKitDashboardContextType = {
  // Chatbot state
  chatbotOpen: boolean
  toggleChatbot: () => void
  openChatbot: () => void
  closeChatbot: () => void
  isChatbotEnabled: boolean
  chatbotPortalRoot: HTMLElement

  // Omnisearch state
  omnisearchOpen: boolean
  toggleOmnisearch: () => void
  openOmnisearch: () => void
  closeOmnisearch: () => void
  isOmnisearchEnabled: boolean
  omnisearchPortalRoot: HTMLElement
}

const AIKitDashboardContext = createContext<AIKitDashboardContextType | null>(null)

type AIKitDashboardProviderProps = {
  children: React.ReactNode
}

export const AIKitDashboardProvider: React.FC<AIKitDashboardProviderProps> = ({ children }) => {
  // Chatbot state
  const [chatbotOpen, setChatbotOpen] = useState(false)
  const chatbotPortalRoot = usePortal('chatbot-portal-root')
  const { hasFeatureFlag } = usePlatformFeatureFlags()
  const isChatbotEnabled = hasFeatureFlag('dashboard:fw_chatbot')

  const toggleChatbot = useCallback(() => setChatbotOpen((prev) => !prev), [])
  const openChatbot = useCallback(() => setChatbotOpen(true), [])
  const closeChatbot = useCallback(() => setChatbotOpen(false), [])

  // Omnisearch state
  const [omnisearchOpen, setOmnisearchOpen] = useState(false)
  const omnisearchPortalRoot = usePortal('omnisearch-portal-root')
  const adminMode = useAdminModeContext()
  const isOmnisearchEnabled =
    hasFeatureFlag('dashboard:fw_omnisearch') || adminMode?.isInternalAdmin || false

  const toggleOmnisearch = useCallback(() => setOmnisearchOpen((prev) => !prev), [])
  const openOmnisearch = useCallback(() => setOmnisearchOpen(true), [])
  const closeOmnisearch = useCallback(() => setOmnisearchOpen(false), [])

  // Block scrolling when omnisearch is open
  React.useEffect(() => {
    const htmlElement = document.documentElement

    if (omnisearchOpen) {
      htmlElement.style.overflow = 'hidden'
    } else {
      htmlElement.style.overflow = ''
    }

    return () => {
      htmlElement.style.overflow = ''
    }
  }, [omnisearchOpen])

  const value = useMemo(
    () => ({
      chatbotOpen,
      toggleChatbot,
      openChatbot,
      closeChatbot,
      isChatbotEnabled,
      chatbotPortalRoot,
      omnisearchOpen,
      toggleOmnisearch,
      openOmnisearch,
      closeOmnisearch,
      isOmnisearchEnabled,
      omnisearchPortalRoot,
    }),
    [
      chatbotOpen,
      toggleChatbot,
      openChatbot,
      closeChatbot,
      isChatbotEnabled,
      chatbotPortalRoot,
      omnisearchOpen,
      toggleOmnisearch,
      openOmnisearch,
      closeOmnisearch,
      isOmnisearchEnabled,
      omnisearchPortalRoot,
    ],
  )

  return <AIKitDashboardContext.Provider value={value}>{children}</AIKitDashboardContext.Provider>
}

export const useAIKitDashboard = (): AIKitDashboardContextType => {
  const context = useContext(AIKitDashboardContext)
  if (!context) {
    throw new Error('useAIKitDashboard must be used within AIKitDashboardProvider')
  }

  return context
}
